import { func, object, string, number } from 'prop-types';
import { useEffect, type ChangeEvent } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { NumberInput } from '@hivebrite/design-system/Inputs';

import InputTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/InputTitle';
import type { ComponentSettingsTab } from 'hb-builder/types/settings';

// Empty input is null to allow user to clear the input value
const EMPTY_INPUT = null;

const handleZeroValue = (input: number) => !input && input !== 0;

const handleNumberValue = (input: string) => {
  const numberValue = parseInt(input, 10);

  if (
    handleZeroValue(numberValue) ||
    Number.isNaN(numberValue) ||
    numberValue < 0
  ) {
    return EMPTY_INPUT;
  }

  return numberValue;
};

export interface NumberSettingProps {
  defaultValue?: number | null;
  disabled?: boolean;
  item: ComponentSettingsTab['item'];
  onChange: ComponentSettingsTab['onChange'];
  settingKey: string;
  title: string;
}

const NumberSetting = ({
  defaultValue = 0,
  disabled = false,
  item,
  onChange,
  settingKey,
  title,
}: NumberSettingProps) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    onChange({
      id: item.id,
      settingKey: `attributes.${settingKey}`,
      value: handleNumberValue(inputValue),
    });
  };

  const handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    resetToDefaultValue(inputValue);
  };

  const resetToDefaultValue = (value) => {
    if (onChange && handleNumberValue(value) === EMPTY_INPUT) {
      onChange({
        id: item.id,
        settingKey: `attributes.${settingKey}`,
        value: defaultValue,
      });
    }
  };

  // Reset the value when the component is unmounted while empty
  useEffect(
    () => () => resetToDefaultValue(item?.attributes?.[settingKey]),
    [],
  );

  return (
    <Wrapper>
      <InputTitle
        boxProps={{ mr: 3 }}
        id={`settingsNumberTitle-${title}-${item.id}`}
        label={title}
      />
      <NumberInput
        disabled={disabled}
        id={`settingsNumber-${title}-${item.id}`}
        ariaLabel={title}
        name={`settingsNumber-${item.id}`}
        onChange={handleChange}
        onBlur={handleBlur}
        value={item?.attributes?.[settingKey] ?? ''}
        variant="small"
        width={155}
      />
    </Wrapper>
  );
};

export default NumberSetting;

const Wrapper = styled(Box).attrs(() => ({
  alignItems: 'center',
  justifyContent: 'space-between',
  mb: 4,
}))``;

NumberSetting.propTypes = {
  item: object.isRequired,
  onChange: func.isRequired,
  settingKey: string.isRequired,
  title: string.isRequired,
  defaultValue: number,
};
